<template>
  <div class="bg-gray-50 dark:bg-black px-6 py-4 border-t border-gray-200 dark:border-gray-700">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div class="flex items-center text-sm text-gray-700 dark:text-gray-300 order-2 sm:order-1">
        <span>Showing {{ from }} to {{ to }} of {{ total }} results</span>
      </div>
      <div class="flex items-center justify-center sm:justify-end space-x-2 order-1 sm:order-2">
        <button
          @click="$emit('prev')"
          :disabled="currentPage === 1"
          class="px-3 py-1 text-sm bg-white dark:bg-black border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <!-- Page Numbers -->
        <div class="flex items-center space-x-1">
          <template v-for="page in visiblePagesWithEllipsis" :key="page">
            <button
              v-if="page !== '...'"
              @click="$emit('goto', page)"
              :class="[
                'px-3 py-1 text-sm rounded-md transition-colors duration-150',
                page !== currentPage
                  ? 'bg-white dark:bg-black border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  : 'text-white'
              ]"
              :style="page === currentPage ? {
                backgroundColor: 'var(--brand-primary)',
                borderColor: 'var(--brand-primary)'
              } : {}"
              @mouseover="(e) => {
                if (page === currentPage) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--brand-hover)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--brand-hover)';
                }
              }"
              @mouseout="(e) => {
                if (page === currentPage) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--brand-primary)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--brand-primary)';
                }
              }"
            >
              {{ page }}
            </button>
            <span v-else class="px-2 py-1 text-sm text-gray-500 dark:text-gray-400">
              ...
            </span>
          </template>
        </div>

        <button
          @click="$emit('next')"
          :disabled="currentPage === lastPage"
          class="px-3 py-1 text-sm bg-white dark:bg-black border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}>();

defineEmits<{
  prev: [];
  next: [];
  goto: [page: number];
}>();

const from = computed(() => {
  return (props.currentPage - 1) * props.perPage + 1;
});

const to = computed(() => {
  return Math.min(props.currentPage * props.perPage, props.total);
});

// Visible pages with ellipsis support (like reference project)
const visiblePagesWithEllipsis = computed(() => {
  const pages: (number | string)[] = [];
  const maxVisiblePages = 5;
  const halfVisible = Math.floor(maxVisiblePages / 2);

  let startPage = Math.max(1, props.currentPage - halfVisible);
  let endPage = Math.min(props.lastPage, startPage + maxVisiblePages - 1);

  // Adjust startPage if needed
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  // Add first page and ellipsis if needed
  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) {
      pages.push('...');
    }
  }

  // Add visible pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis and last page if needed
  if (endPage < props.lastPage) {
    if (endPage < props.lastPage - 1) {
      pages.push('...');
    }
    pages.push(props.lastPage);
  }

  return pages;
});
</script>

